/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {Directive, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef} from '@angular/core';
import {
    AbstractControl,
    AbstractControlOptions,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidatorFn
} from '@angular/forms';
import { v4 as uuid } from 'uuid';
import {WarningsEnum} from '../../warnings/warnings.enum';
import {Subscription} from 'rxjs';
import {InputControl} from './input-control';

@Directive()
/**
 * @todo CTWILS-3963 - Check TODOs on properties. Also check InputRadioComponentComponent for a possible implementation.
 */
export abstract class InputComponent implements OnInit, OnDestroy {
    @Input() showViewMode = false;
    @Input() invertedColor = false;
    @Input() height = 30;

    @Input() labelInformation: TemplateRef<any>;
    @Input() label: string;
    @Input() warningLevel: WarningsEnum;
    @Input() showMessagesWhenDisabled = false;
    @Input() messages = new Map<WarningsEnum, string[]>();
    @Input() placeholder = '';
    @Input() baseCypressTag: string;
    @Input() isDisabled = false;
    /**
     * @todo Extend from {@link StatefulComponent}, since it already holds state.
     */
    @Input() state: any;
    @Input() validators: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null;
    /**
     * @todo Move this to the appropriate inputs, this isn't applicable to all inputs
     */
    @Input() maxLength: number;
    /**
     * @todo Remove "any" types where possible, make the class generic and use generic types.
     */
    @Input() initialValue: any = null;
    @Input() alternateBackground = false;
    @Input() hasLabel = true;
    @Input() isSmallLabel = false;
    /**
     * @todo Change this "any" to the union type of {@link InputComponent.initialValue} and string.
     * @todo Example: <code>EventEmitter&lt;T | string&gt;</code>
     */
    @Output() inputStatusChange = new EventEmitter<any>();
    @Output() inputFocus = new EventEmitter<Event>();
    @Output() inputClick = new EventEmitter<Event>();
    @Output() inputBlur = new EventEmitter<Event>();
    @Output() inputKeyDown = new EventEmitter<Event>();
    /**
     * @todo Change this "any" to the union type of {@link InputComponent.initialValue} and string.
     * @todo Example: <code>EventEmitter&lt;T | string&gt;</code>
     */
    @Output() inputValueChange = new EventEmitter<any>();
    /**
     * @todo Change this "any" to the union type of {@link InputComponent.initialValue} and string.
     * @todo Example: <code>EventEmitter&lt;T | string&gt;</code>
     */
    @Output() inputStateChange = new EventEmitter<InputControl<any>>();

    @Input() hasSearchIcon = false;

    public form: UntypedFormGroup;
    public readonly HTML_ID: string;
    public readonly FORM_CONTROL_NAME = 'input';
    public readonly DELETE_ICON_CLASS = 'iwp-icon-gen_close_l';
    public readonly SEARCH_ICON_CLASS = 'iwp-icon-gen_search';
    public isValid = true;
    public isFocused = false;
    public disableTooltip = true;
    /**
     * @todo Change this "any" to a proper type. It seems like a string, but needs further investigation.
     */
    public tooltipText: any;
    protected subscriptions = new Subscription();

    get inputControl(): AbstractControl {
        if (this.form == null) {
            return null;
        }

        return this.form.get(this.FORM_CONTROL_NAME);
    }

    get hasWarningStar(): boolean {
        return this.warningLevel != null;
    }

    get hasDeleteButton(): boolean {
        return !this.isDisabled &&
                this.inputControl.value != null
                && this.inputControl.value !== '';
    }

    get textInputCypressTag(): string {
        return `${this.baseCypressTag}-input`;
    }

    get deleteButtonCypressTag(): string {
        return `${this.baseCypressTag}-delete-button`;
    }

    get warningInfoCypressTag(): string {
        return `${this.baseCypressTag}-warning-info`;
    }

    get warningStarCypressTag(): string {
        return `${this.baseCypressTag}-warning-star`;
    }

    get labelInformationCypressTag(): string {
        return `${this.baseCypressTag}-label-information`;
    }

    get inputWrapperCypressTag(): string {
        return `${this.baseCypressTag}-input-wrapper`;
    }

    get inputValueCypressTag(): string {
        return `${this.baseCypressTag}-value`;
    }

    get labelCypressTag(): string {
        return `${this.baseCypressTag}-label`;
    }

    get isInputDisabled(): boolean {
        return this.inputControl.disabled;
    }

    get deleteButtonIconClass(): string {
        return this.DELETE_ICON_CLASS;
    }

    get showMessages(): boolean {
        return !this.isDisabled || this.showMessagesWhenDisabled;
    }

    get inputHasEllipsis(): boolean {
        return this.disableTooltip;
    }

    get inputHeight(): { [key: string]: string } {
        return {'height': `${this.height}px`};
    }

    protected constructor(protected formBuilder: UntypedFormBuilder) {
        this.HTML_ID = uuid()
                .replace(/-/g, '');
    }

    deleteInputValue(): void {
        if (this.inputControl == null) {
            return;
        }
        this.inputControl.setValue(null);
        this.inputControl.markAsTouched();
        this.inputStateChange.emit(this.inputControl);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnInit(): void {
        this.setupForm();
        this.subscriptions.add(this.inputControl.valueChanges.subscribe((event) => {
            this.isValid = this.inputControl.valid || this.inputControl.disabled;
            this.isDisabled = this.inputControl.disabled;
            this.inputValueChange.emit(event);
            this.inputStateChange.emit(this.inputControl);
            this.tooltipText = event;
        }));

        this.subscriptions.add(this.inputControl.statusChanges.subscribe((event) => {
            this.isValid = this.inputControl.valid || this.inputControl.disabled;
            this.isDisabled = this.inputControl.disabled;
            this.inputStatusChange.emit(event);
            this.inputStateChange.emit(this.inputControl);
        }));

        this.subscriptions.add(this.inputFocus.subscribe(() => {
            this.inputStateChange.emit(this.inputControl);
            this.isFocused = true;
        }));

        this.subscriptions.add(this.inputClick.subscribe(() => {
            this.inputStateChange.emit(this.inputControl);
        }));

        this.subscriptions.add(this.inputBlur.subscribe(() => {
            this.inputStateChange.emit(this.inputControl);
            this.isFocused = false;
        }));

        this.subscriptions.add(this.inputKeyDown.subscribe(() => {
            this.inputStateChange.emit(this.inputControl);
        }));
    }

    disable(): void {
        if (this.inputControl == null) {
            return;
        }

        this.inputControl.disable();
    }

    enable(): void {
        if (this.inputControl == null) {
            return;
        }

        this.inputControl.enable();
    }

    refresh(): void {
        if (this.inputControl == null) {
            return;
        }

        this.inputControl.setValue(this.inputControl.value);
    }

    setValue(value: any, options?: { emitEvent: boolean }): void {
        if (this.inputControl == null) {
            return;
        }
        if (!options && this.showViewMode) {
            options = {emitEvent: false};
        }
        this.inputControl.setValue(value, options);
    }

    hasValue(): boolean {
        return this.inputControl != null && this.inputControl.value != null;
    }

    reset(): void {
        if (this.inputControl == null) {
            return;
        }
        this.inputControl.reset();
    }

    markAsInvalid(): void {
        if (this.inputControl == null) {
            return;
        }
        this.inputControl.setErrors({'invalid': true});
        this.isValid = false;
    }

    markAsValid(): void {
        if (this.inputControl == null) {
            return;
        }
        this.inputControl.setErrors(null);
        this.isValid = true;
    }

    toggleTooltip = (event: any): void => {
        if (this.isValid && this.tooltipText != null) {
            const element = event.target as Element;
            this.disableTooltip = !(element.scrollWidth > element.clientWidth);
        } else {
            this.disableTooltip = true;
        }
    };

    protected setInputValidator(validator: ValidatorFn): void {
        if (this.validators == null) {
            this.inputControl.setValidators([validator]);
        } else if (!(this.validators instanceof Array)) {
            this.inputControl.setValidators([(this.validators as ValidatorFn), validator]);
        } else {
            this.inputControl.setValidators([...(this.validators), validator]);
        }
        this.inputControl.updateValueAndValidity({emitEvent: false});
    }

    private setupForm(): void {
        const initialState: any = {value: this.initialValue, disabled: this.isDisabled};
        const finalState = Object.assign(initialState, this.state);

        this.form = this.formBuilder.group({
            'input': new UntypedFormControl(finalState, this.validators)
        });

        this.inputStateChange.emit(finalState);
    }
}
